import { lazy, Suspense } from 'react'
import { LoadingScreen } from '@/components/ui/loading-screen'

// Each role's dashboard pulls its own heavy dependencies (charts, AI tutor),
// so only the one the user needs is downloaded.
const StudentDashboard = lazy(() => import('@/components/dashboard/StudentDashboard').then(m => ({ default: m.StudentDashboard })))
const TeacherDashboard = lazy(() => import('@/components/dashboard/TeacherDashboard').then(m => ({ default: m.TeacherDashboard })))
const AdminDashboard = lazy(() => import('@/components/dashboard/AdminDashboard').then(m => ({ default: m.AdminDashboard })))
const ParentDashboard = lazy(() => import('@/components/dashboard/ParentDashboard').then(m => ({ default: m.ParentDashboard })))
import { useAuth } from '@/hooks/use-auth'
import { useToast } from '@/hooks/use-toast'
import { useTranslation } from 'react-i18next'
import { useEffect } from 'react'
import { Navigate } from 'react-router-dom'

export function DashboardPage() {
  const { user, isLoading } = useAuth()
  const { toast } = useToast()
  const { t } = useTranslation()

  // Check if user just registered and show welcome message
  useEffect(() => {
    const justRegistered = sessionStorage.getItem('just_registered')
    const registeredEmail = sessionStorage.getItem('registered_email')

    if (justRegistered === 'true' && user) {
      toast({
        title: t('dashboard.welcomeTitle', '🎉 Welcome to AppAcademia!'),
        description: t('dashboard.welcomeDescription', { email: registeredEmail || user.email, defaultValue: `Successfully logged in as ${registeredEmail || user.email}. Start your learning journey!` }),
      })

      // Clear the flags
      sessionStorage.removeItem('just_registered')
      sessionStorage.removeItem('registered_email')
    }
  }, [user?.id, toast, t])

  if (isLoading || !user) {
    return (
      <div className="container mx-auto px-4 py-6 space-y-4 animate-pulse">
        <div className="h-8 bg-gray-200 rounded w-1/3" />
        <div className="h-4 bg-gray-100 rounded w-1/2" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-32 bg-gray-200 rounded-xl" />
          ))}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[1, 2].map(i => (
            <div key={i} className="h-48 bg-gray-100 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  // Get user role - priority: primary_role (for trial users) or first role
  const userRole = user.primary_role || user.roles?.[0]

  // Show appropriate dashboard based on user role
  switch (userRole) {
    case 'student':
      return (
        <div className="container mx-auto px-4 py-6">
          {/* {showDebug && <TokenDebugPanel />} */}
          <Suspense fallback={<LoadingScreen />}><StudentDashboard /></Suspense>
        </div>
      );

    case 'teacher':
      return (
        <div className="container mx-auto px-4 py-6">
           {/* {showDebug && <TokenDebugPanel />} */}
          <Suspense fallback={<LoadingScreen />}><TeacherDashboard /></Suspense>
        </div>
      );

    case 'admin':
    case 'school_admin':
      return (
        <div className="container mx-auto px-4 py-6">
           {/* {showDebug && <TokenDebugPanel />} */}
          <Suspense fallback={<LoadingScreen />}><AdminDashboard /></Suspense>
        </div>
      );

    case 'parent':
      return (
        <div className="container mx-auto px-4 py-6">
           {/* {showDebug && <TokenDebugPanel />} */}
          <Suspense fallback={<LoadingScreen />}><ParentDashboard /></Suspense>
        </div>
      );

    case 'guard':
    case 'bus_driver':
      return <Navigate to="/app/attendance" replace />;

    case 'cafeteria_staff':
      return <Navigate to="/app/cafeteria" replace />;

    case 'emf_agent':
      return <Navigate to="/app/emf-portal" replace />;

    default:
      // Default to student dashboard for demo
      return (
        <div className="container mx-auto px-4 py-6">
          {/* {showDebug && <TokenDebugPanel />} */}
          <Suspense fallback={<LoadingScreen />}><StudentDashboard /></Suspense>
        </div>
      );
  }
}
